import { AsyncPipe, DecimalPipe, Location, SlicePipe } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { InstrumentDetailFacade } from '@zero/instrument-details/domain';
import { InstrumentSearchApiService } from '@zero/search/api-instrument-search';
import { Asset, AssetTimelineParams } from '@zero/shared/util-models';
import { MetaDataService } from '@zero/shared/util-web-app-services';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, filter, firstValueFrom, map } from 'rxjs';
import { AssetTimelineItemComponent } from '../asset-timeline-item/asset-timeline-item.component';

@Component({
  selector: 'instrument-details-instrument-asset-timeline',
  templateUrl: './asset-timeline-web.component.html',
  styleUrls: ['./asset-timeline-web.component.scss'],
  imports: [AssetTimelineItemComponent, TranslocoPipe, DecimalPipe, AsyncPipe, SlicePipe],
})
export class AssetTimelineComponent implements OnInit, OnDestroy {
  public readonly location = inject(Location);
  public readonly facade = inject(InstrumentDetailFacade);
  readonly #route = inject(ActivatedRoute);
  readonly #metaDataService = inject(MetaDataService);
  readonly #assetService = inject(InstrumentSearchApiService);
  readonly #destroyRef = inject(DestroyRef);

  public asset: Asset;

  #params: AssetTimelineParams = {
    isin: '',
    page: '0',
    size: '10',
  };

  readonly #currentPage: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  #currentPage$: Observable<number> = this.#currentPage.asObservable();

  ngOnInit() {
    this.#init();
  }

  ngOnDestroy(): void {
    this.facade.clearAssetTimeline();
  }

  #init = async () => {
    this.#params.isin = this.#route.snapshot.paramMap.get('id');
    this.asset = await firstValueFrom(this.#assetService.getAssetByIsin(this.#params.isin));
    if (!this.asset?.name) {
      this.location.back();
      return;
    }

    this.#metaDataService.setTitle(`${this.asset.name} | ${this.asset.instrumentType}`);

    this.#subscribeToCurrentPage();
  };

  public nextPage = () => this.#currentPage.next(this.#currentPage.value + 1);

  #subscribeToCurrentPage = () => {
    combineLatest({
      currentPage: this.#currentPage$,
      hasNext: this.facade.assetTimelineHasNext$,
      assetTimeline: this.facade.assetTimeline$,
    })
      .pipe(
        filter(({ currentPage, hasNext, assetTimeline }) => {
          const isInit = currentPage === 0 && !assetTimeline?.items?.pageable;
          const isPaging = !!hasNext && currentPage !== assetTimeline?.items?.pageable?.pageNumber;
          return isInit || isPaging;
        }),
        map(({ currentPage }) => currentPage),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((page) => this.#loadHistory(page));
  };

  #loadHistory = (page?: number) => {
    this.#params.page = page ? page.toString() : this.#params.page;
    this.#params.isin && this.facade.loadAssetTimeline(this.#params, true);
  };
}
